import { Query } from "appwrite";

import {
  APPWRITE_DATABASE_ID,
  PROJECT_PRESENCE_TABLE_ID,
  client,
  tablesDB
} from "src/handlers/Appwrite";
import { listAllRows } from "src/handlers/Appwrite/utils";

import {
  ROUTER_TYPE,
  getCurrentUserId,
  getPresencePermissions,
  getProjectId,
  makePresenceRowId,
  parseJson,
  stringify
} from "./internal";

// ---------------------------------------------------------------------------
// Presence heartbeat write queue.
//
// Heartbeats fire at PRESENCE_HEARTBEAT_MS (~2-3s). Without coalescing we'd
// hammer Appwrite with many redundant writes, so each row keeps a small
// in-memory state machine:
//
//   - `running`        \u2014 a write is in flight
//   - `queuedData`     \u2014 next payload to write once the inflight settles
//   - `lastSignature`  \u2014 stringified payload of the last successful write,
//                        used to suppress duplicates
//   - `exists`         \u2014 best-guess of whether the server row exists, so we
//                        skip a doomed update on the first call
//
// `writePresenceRow` itself recovers from 404 (row vanished mid-flight) and
// 409 (concurrent create from another tab/device).
// ---------------------------------------------------------------------------

const PRESENCE_DEDUP_WINDOW_MS = 500;

const presenceWriteState = new Map();

function getPresenceState(rowId) {
  if (!presenceWriteState.has(rowId)) {
    presenceWriteState.set(rowId, {
      running: false,
      queuedData: null,
      queuedPermissions: null,
      exists: false,
      lastSignature: "",
      lastWrittenAtMs: 0,
      promise: Promise.resolve()
    });
  }

  return presenceWriteState.get(rowId);
}

async function writePresenceRow(rowId, data, permissions) {
  const state = getPresenceState(rowId);

  if (!state.exists) {
    try {
      const result = await tablesDB.createRow({
        databaseId: APPWRITE_DATABASE_ID,
        tableId: PROJECT_PRESENCE_TABLE_ID,
        rowId,
        data,
        permissions
      });
      state.exists = true;
      return result;
    } catch (error) {
      if (error?.code !== 409) {
        throw error;
      }
      state.exists = true;
    }
  }

  try {
    const result = await tablesDB.updateRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PRESENCE_TABLE_ID,
      rowId,
      data
    });
    state.exists = true;
    return result;
  } catch (error) {
    if (error?.code !== 404) {
      throw error;
    }
    state.exists = false;
  }

  try {
    const result = await tablesDB.createRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PRESENCE_TABLE_ID,
      rowId,
      data,
      permissions
    });
    state.exists = true;
    return result;
  } catch (error) {
    if (error?.code === 409) {
      state.exists = true;
      return tablesDB.updateRow({
        databaseId: APPWRITE_DATABASE_ID,
        tableId: PROJECT_PRESENCE_TABLE_ID,
        rowId,
        data
      });
    }

    throw error;
  }
}

async function flushPresenceQueue(rowId) {
  const state = getPresenceState(rowId);
  state.running = true;

  while (state.queuedData) {
    const data = state.queuedData;
    const permissions = state.queuedPermissions;
    const signature = JSON.stringify(data);

    state.queuedData = null;
    state.queuedPermissions = null;

    await writePresenceRow(rowId, data, permissions);
    state.lastSignature = signature;
    state.lastWrittenAtMs = Date.now();
  }

  state.running = false;
}

async function listPresenceRows() {
  const projectId = getProjectId();
  if (!projectId) return [];

  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECT_PRESENCE_TABLE_ID,
    queries: [
      Query.equal("projectId", [projectId]),
      Query.orderDesc("lastSeenAtMs")
    ]
  });
}

function mapPresenceRow(row) {
  const rawSectionTab = row.sectionTab;
  const numericTab = Number(rawSectionTab);

  return {
    clientId: row.clientId,
    name: row.name,
    color: parseJson(row.avatarJson, {})?.color,
    page: row.currentPage,
    tabId: row.currentDeviceId,
    sectionTab: Number.isNaN(numericTab) ? (rawSectionTab || 0) : numericTab,
    fieldKey: row.fieldKey || "",
    fieldLabel: row.fieldLabel || "",
    lastSeen: row.lastSeenAtMs
  };
}

export async function upsertPresence(presence) {
  const projectId = getProjectId();
  const userId = getCurrentUserId();
  if (!projectId || !presence?.clientId || !userId) return;

  const rowId = makePresenceRowId(projectId, presence.clientId);
  const permissions = getPresencePermissions();
  const data = {
    projectId,
    clientId: presence.clientId,
    userId,
    name: presence.name || "",
    email: "",
    avatarJson: stringify({ color: presence.color }),
    role: presence.role || "read",
    currentPage: presence.page || ROUTER_TYPE,
    currentDeviceType: presence.deviceType || presence.page || ROUTER_TYPE,
    currentDeviceId: Number(presence.tabId || 0),
    sectionTab: Number(presence.sectionTab) || 0,
    fieldKey: presence.fieldKey || "",
    fieldLabel: presence.fieldLabel || "",
    lastSeenAtMs: Date.now()
  };
  const signature = JSON.stringify(data);
  const state = getPresenceState(rowId);

  // Suppress duplicate writes within the dedup window when nothing is in
  // flight \u2014 saves a round-trip when the user hovers the same field.
  if (
    !state.running &&
    state.lastSignature === signature &&
    Date.now() - state.lastWrittenAtMs < PRESENCE_DEDUP_WINDOW_MS
  ) {
    return state.promise;
  }

  state.queuedData = data;
  state.queuedPermissions = permissions;

  if (!state.running) {
    state.promise = flushPresenceQueue(rowId).catch((error) => {
      state.running = false;
      throw error;
    });
  }

  return state.promise;
}

export async function clearPresence(clientId) {
  const projectId = getProjectId();
  if (!projectId || !clientId) return;
  const rowId = makePresenceRowId(projectId, clientId);
  const state = presenceWriteState.get(rowId);
  if (state) {
    state.queuedData = null;
    state.queuedPermissions = null;
    state.exists = false;
  }

  try {
    await tablesDB.deleteRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PRESENCE_TABLE_ID,
      rowId
    });
  } catch (error) {
    if (error?.code !== 404) throw error;
  }
}

export function subscribePresence(callback) {
  const projectId = getProjectId();
  if (!projectId) return () => {};

  const users = {};
  let disposed = false;

  const emitPresence = () => {
    if (disposed) return;
    callback?.({ ...users });
  };

  listPresenceRows()
    .then((rows) => {
      if (disposed) return;

      rows.forEach((row) => {
        users[row.clientId] = mapPresenceRow(row);
      });

      emitPresence();
    })
    .catch((error) => {
      console.error("Failed to refresh presence", error);
    });

  const unsubscribe = client.subscribe(
    `databases.${APPWRITE_DATABASE_ID}.tables.${PROJECT_PRESENCE_TABLE_ID}.rows`,
    (event) => {
      const row = event?.payload;
      if (!row || row.projectId !== projectId) return;

      const isDelete = event?.events?.some((name) => String(name).includes(".delete"));
      if (isDelete) {
        delete users[row.clientId];
      } else {
        users[row.clientId] = mapPresenceRow(row);
      }

      emitPresence();
    }
  );

  return () => {
    disposed = true;
    unsubscribe?.();
  };
}

// Internal exports for tests \u2014 lets the presence test suite reset queue
// state between cases without needing to reload the module.
export const __test__ = {
  presenceWriteState,
  writePresenceRow,
  flushPresenceQueue,
  mapPresenceRow,
  listPresenceRows,
  PRESENCE_DEDUP_WINDOW_MS
};
